import { useMemo, useState } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@tark/shared/components';
import {
  useAuth,
  useScheduleDashboard,
  useTenantBootstrap,
} from '@tark/shared/hooks';
import type {
  ScheduleCoachDashboardAttentionItem,
  ScheduleDashboardOccurrence,
} from '@tark/shared/types';
import { CheckCircle2, ListChecks, Loader2, Users, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import SessionAttendancePanel from '../components/coach-studio/SessionAttendancePanel';
import CoachPageHeader from '../components/CoachPageHeader';

const MANAGE_ROLES = ['OWNER', 'ADMIN', 'COACH', 'STAFF'];

export default function CoachAttendancePage() {
  const { user } = useAuth();
  const { activeTenant } = useTenantBootstrap(Boolean(user));
  const isCoachStudio = activeTenant?.tenant?.tenantArchetype === 'COACHING';
  const canManage = MANAGE_ROLES.includes(activeTenant?.roleCode?.toUpperCase() || '');
  const dashboard = useScheduleDashboard(Boolean(user) && isCoachStudio);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<number | null>(null);
  const requestedOccurrenceId = Number(searchParams.get('occurrenceId'));
  const queryOccurrenceId = Number.isFinite(requestedOccurrenceId) && requestedOccurrenceId > 0 ? requestedOccurrenceId : null;

  const attendanceItems = useMemo(
    () => (dashboard.data?.coachDashboard?.attentionItems ?? []).filter(isAttendanceAttention),
    [dashboard.data?.coachDashboard?.attentionItems]
  );
  const upcoming = dashboard.data?.upcomingOccurrences ?? [];
  const nextSessions = upcoming.slice(0, 8);
  const todayCount = upcoming.filter(isToday).length;
  const effectiveOccurrenceId =
    selectedOccurrenceId ??
    queryOccurrenceId ??
    attendanceItems.find((item) => item.occurrenceId)?.occurrenceId ??
    nextSessions[0]?.occurrenceId ??
    null;
  const selectOccurrence = (occurrenceId: number) => {
    setSelectedOccurrenceId(occurrenceId);
    setSearchParams({ occurrenceId: String(occurrenceId) }, { replace: true });
  };

  if (!isCoachStudio || !canManage) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ListChecks className="h-6 w-6" />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Coach Studio</p>
              <h1 className="mt-2 text-2xl font-semibold tracking-tight text-text">Attendance needs a Coach Studio</h1>
              <p className="mt-2 text-sm text-text-muted">
                Switch into an enabled coach workspace to review session rosters and mark attendance.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (dashboard.isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (dashboard.isError) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card variant="default">
          <CardContent className="space-y-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-500/10 text-red-500">
              <XCircle className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-text">Attendance unavailable</h1>
              <p className="mt-2 text-sm text-text-muted">Refresh and we will restore the attendance view.</p>
            </div>
            <Button variant="outline" onClick={() => dashboard.refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-24 md:pb-8">
      <CoachPageHeader
        title="Attendance"
        subtitle="Choose a session, mark check-in, and save quick coach notes without leaving this page."
        meta={
          <span className="rounded-full border border-border bg-surface px-3 py-1 text-xs font-semibold text-text-muted">
            {activeTenant?.tenant?.displayName || 'Coach Studio'}
          </span>
        }
        stats={
          <div className="grid gap-3 sm:grid-cols-3">
            <MiniStat label="Today" value={todayCount} />
            <MiniStat label="Upcoming" value={upcoming.length} />
            <MiniStat label="Needs attention" value={attendanceItems.length} />
          </div>
        }
      />

      <div className="grid gap-5 xl:grid-cols-[0.8fr_1.2fr]">
        <div className="space-y-5">
          {attendanceItems.length > 0 ? (
            <Card variant="default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  Attendance queue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {attendanceItems.map((item) => (
                  <AttendanceItem
                    key={item.key}
                    item={item}
                    selected={Boolean(item.occurrenceId && item.occurrenceId === effectiveOccurrenceId)}
                    onSelect={() => item.occurrenceId && selectOccurrence(item.occurrenceId)}
                  />
                ))}
              </CardContent>
            </Card>
          ) : null}

          <Card variant="default">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-5 w-5 text-primary" />
                Sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {nextSessions.length > 0 ? (
                nextSessions.map((occurrence) => (
                  <SessionRow
                    key={occurrence.occurrenceId}
                    occurrence={occurrence}
                    selected={occurrence.occurrenceId === effectiveOccurrenceId}
                    onSelect={() => selectOccurrence(occurrence.occurrenceId)}
                  />
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-border bg-surface-elevated/20 px-4 py-8 text-center">
                  <p className="font-semibold text-text">No sessions yet</p>
                  <p className="mt-1 text-sm text-text-muted">Create a class and schedule the first session before taking attendance.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {effectiveOccurrenceId ? (
          <SessionAttendancePanel
            occurrenceId={effectiveOccurrenceId}
            emptyRosterMessage="Add students from Schedule before marking attendance."
          />
        ) : (
          <Card variant="default">
            <CardContent className="rounded-xl border border-dashed border-border bg-surface-elevated/20 px-4 py-10 text-center">
              <p className="font-semibold text-text">Choose a session</p>
              <p className="mt-1 text-sm text-text-muted">The roster and check-in controls appear here.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function AttendanceItem({
  item,
  selected,
  onSelect,
}: {
  item: ScheduleCoachDashboardAttentionItem;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!item.occurrenceId}
      onClick={onSelect}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        selected
          ? 'border-primary/40 bg-primary/10'
          : 'border-border bg-surface hover:border-primary/30 hover:bg-surface-elevated'
      }`}
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-text">{item.title}</span>
        <span className="mt-1 block text-xs leading-5 text-text-muted">{item.description}</span>
      </span>
      {item.count != null ? (
        <span className="shrink-0 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          {item.count}
        </span>
      ) : null}
    </button>
  );
}

function SessionRow({
  occurrence,
  selected,
  onSelect,
}: {
  occurrence: ScheduleDashboardOccurrence;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
        selected
          ? 'border-primary/40 bg-primary/10'
          : 'border-border bg-surface hover:border-primary/30 hover:bg-surface-elevated'
      }`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-text">{occurrence.title}</span>
        <span className="mt-1 block truncate text-xs text-text-muted">
          {[formatDateRange(occurrence.startsAt, occurrence.endsAt), occurrence.locationLabel, occurrence.coachName].filter(Boolean).join(' / ')}
        </span>
      </span>
      <span className="shrink-0 rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-text-muted">
        Roster
      </span>
    </button>
  );
}

function MiniStat({ label, value }: { label: number | string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-text-subtle">{label}</p>
      <p className="mt-1 text-xl font-semibold leading-none text-text">{value}</p>
    </div>
  );
}

function isAttendanceAttention(item: ScheduleCoachDashboardAttentionItem) {
  const text = `${item.key} ${item.title} ${item.description} ${item.actionKey}`.toLowerCase();
  return text.includes('attendance') || text.includes('check-in') || text.includes('roster');
}

function isToday(occurrence: ScheduleDashboardOccurrence) {
  const start = new Date(occurrence.startsAt);
  const now = new Date();
  return (
    start.getFullYear() === now.getFullYear() &&
    start.getMonth() === now.getMonth() &&
    start.getDate() === now.getDate()
  );
}

function formatDateRange(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const startTime = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const endTime = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${day} / ${startTime} - ${endTime}`;
}
